/**
 * Interpretación del acuse de una declaración presentada ante el SAT.
 *
 * De aquí sale el dato que hoy nadie lee: si un periodo cerró **a cargo** o
 * **a favor** y por cuánto. El acuse está firmado por el SAT, así que es la
 * mejor fuente disponible; el PDF ya está cargado y solo se guardaba.
 *
 * MÓDULO APARTE de la opinión y de la constancia, por lo mismo que ellas están
 * separadas entre sí: sus estructuras no coinciden y un intérprete único con un
 * parámetro de "modo" sería justo donde se cuela el error de leer un documento
 * con las reglas de otro.
 *
 * DOS RAREZAS DE ESTE PDF, que los de Salud SAT no tienen:
 *
 * 1. **El texto llega letra a letra.** El acuse alterna dos fuentes y buena
 *    parte de las etiquetas sale como `A c a r g o :` mientras el valor de al
 *    lado llega entero (`3,220`). Por eso TODO el parseo trabaja sobre el texto
 *    SIN ESPACIOS: comparar así no adivina nada, mientras que reconstruir
 *    palabras uniendo letras sueltas funciona hasta que una razón social lleva
 *    iniciales o cambia el maquetado.
 * 2. **Dos fuentes CID con mapas ToUnicode en conflicto** —el código 0004 es
 *    `m` en una y `S` en la otra—. Fusionarlos devuelve texto corrupto; pdf.js
 *    aplica el mapa correcto por fuente. Es la razón de fondo por la que esto
 *    se hace en el navegador y no con expresiones regulares en el servidor.
 *
 * EL SIGNO ES LA ETIQUETA. El acuse no trae campo de signo ni importes
 * negativos: dice `A cargo` o `A favor`. Deducirlo del importe es imposible, y
 * suponer "a cargo" cuando la etiqueta no se reconoce convertiría un fallo de
 * lectura en una deuda inventada. Un concepto sin etiqueta reconocible
 * simplemente no se devuelve.
 */

import { extraerTexto, normalizar } from './pdfTexto';

const MESES = [
  'ENERO', 'FEBRERO', 'MARZO', 'ABRIL', 'MAYO', 'JUNIO',
  'JULIO', 'AGOSTO', 'SEPTIEMBRE', 'OCTUBRE', 'NOVIEMBRE', 'DICIEMBRE',
];

/** Los mismos valores que acepta la whitelist del servidor. */
export const ISR = 'isr';
export const IVA = 'iva';
export const CARGO = 'cargo';
export const FAVOR = 'favor';

/**
 * Texto comparable: mayúsculas, sin acentos y SIN ESPACIOS.
 *
 * Quitar los espacios en lugar de colapsarlos es lo que vuelve indiferente que
 * una etiqueta llegue entera o desmenuzada. Como efecto secundario también
 * arregla los importes partidos: `3 , 2 2 0` queda `3,220`.
 */
const compactar = (texto) => normalizar(texto).replace(/\s+/g, '');

/**
 * Importe del acuse a número.
 *
 * La coma es separador de millar y el punto decimal: `3,220` son tres mil
 * doscientos veinte, no tres con veintidós. Devuelve null si no hay dígitos, y
 * eso significa "no leído", nunca cero.
 */
const aImporte = (crudo) => {
  if (!crudo) return null;
  const limpio = crudo.replace(/,/g, '');
  if (!/^\d+(\.\d+)?$/.test(limpio)) return null;
  const valor = Number(limpio);
  return Number.isFinite(valor) ? valor : null;
};

/**
 * Impuesto del concepto por su descripción.
 *
 * El IVA se comprueba primero porque su descripción es inconfundible; lo que
 * queda por reconocer es el ISR, que el acuse nombra de varias formas según el
 * régimen. Una descripción que no encaje devuelve null y el concepto se
 * descarta: el servidor solo acepta `isr` e `iva`, y adivinar aquí metería un
 * importe ajeno al acumulado.
 */
const impuestoDe = (descripcion) => {
  if (descripcion.includes('IMPUESTOALVALORAGREGADO') || descripcion.includes('IVA')) {
    return IVA;
  }
  if (descripcion.includes('ISR') || descripcion.includes('IMPUESTOSOBRELARENTA')) {
    return ISR;
  }
  return null;
};

/**
 * Etiqueta de signo del concepto: la PRIMERA que aparece en su bloque.
 *
 * `CANTIDADACARGO:` contiene `ACARGO:` y va siempre después. Se descarta por el
 * prefijo y no por el orden, para que un cambio de maquetado no invierta la
 * lectura en silencio.
 */
const leerSigno = (bloque) => {
  const patron = /(CANTIDAD)?A(CARGO|FAVOR):([\d.,]*)/g;
  let m = patron.exec(bloque);

  while (m) {
    if (!m[1]) {
      return {
        signo: m[2] === 'FAVOR' ? FAVOR : CARGO,
        importe: aImporte(m[3]),
        // Dónde termina la descripción del concepto y empiezan los importes.
        indice: m.index,
      };
    }
    m = patron.exec(bloque);
  }

  return null;
};

/**
 * Los conceptos del acuse, uno por impuesto declarado.
 *
 * Cada bloque va de una cabecera `Concepto de pago N:` a la siguiente; la
 * última llega hasta el final del texto, donde el pie legal no estorba porque
 * ya no hay etiquetas de signo que confundir.
 */
const leerConceptos = (texto) => {
  const cabeceras = [...texto.matchAll(/CONCEPTODEPAGO(\d+):/g)];
  const conceptos = [];

  cabeceras.forEach((cabecera, i) => {
    const inicio = cabecera.index + cabecera[0].length;
    const fin = i + 1 < cabeceras.length ? cabeceras[i + 1].index : texto.length;
    const bloque = texto.slice(inicio, fin);

    const etiqueta = leerSigno(bloque);
    // Sin etiqueta reconocible el concepto NO se devuelve. Devolverlo como
    // "a cargo 0" sería inventar una declaración en ceros.
    if (!etiqueta || etiqueta.importe === null) return;

    // El impuesto se busca SOLO en la descripción, no en el bloque entero: sin
    // espacios, cualquier palabra del pie puede esconder un `IVA` o un `ISR`
    // ajenos al concepto.
    const impuesto = impuestoDe(bloque.slice(0, etiqueta.indice));
    if (!impuesto) return;

    const aPagar = bloque.match(/CANTIDADAPAGAR:([\d.,]*)/);

    conceptos.push({
      impuesto,
      signo: etiqueta.signo,
      importe: etiqueta.importe,
      // Informativa: ya viene neteada del acreditamiento que hizo el
      // contribuyente, así que NO entra al acumulado.
      cantidad_a_pagar: aImporte(aPagar?.[1]) ?? 0,
    });
  });

  return conceptos;
};

/**
 * `Período de la declaración: Julio` + `Ejercicio: 2026` -> `2026-07`.
 *
 * El mes se compara contra la lista y no con `[A-Z]+`: sin espacios, el nombre
 * del mes queda pegado a la etiqueta siguiente (`JULIOEJERCICIO`) y cualquier
 * lectura por longitud se llevaría de más.
 */
const leerPeriodo = (texto) => {
  const ejercicio = texto.match(/EJERCICIO:(\d{4})/);
  if (!ejercicio) return null;

  const etiqueta = texto.match(/PERIODODELADECLARACION:/);
  if (!etiqueta) return null;

  const resto = texto.slice(etiqueta.index + etiqueta[0].length);
  const mes = MESES.findIndex((nombre) => resto.startsWith(nombre));
  if (mes === -1) return null;

  return `${ejercicio[1]}-${String(mes + 1).padStart(2, '0')}`;
};

/**
 * Interpreta el TEXTO de un acuse. Puro y sin dependencias: aquí viven los
 * casos difíciles y es lo que se prueba sin montar pdf.js ni React.
 *
 * @param {string} textoCrudo texto extraído del PDF
 * @returns {{rfc: ?string, period: ?string, tipo: ?string, conceptos: Array, motivo: ?string}}
 */
export const interpretarTexto = (textoCrudo) => {
  const texto = compactar(textoCrudo);
  const vacio = { rfc: null, period: null, tipo: null, conceptos: [] };

  if (texto.length < 20) {
    return { ...vacio, motivo: 'sin_texto' };
  }

  // Que el documento se anuncie como acuse de declaración. Sin esto, cualquier
  // PDF con un RFC dentro pasaría por acuse y aportaría conceptos vacíos.
  if (!texto.includes('ACUSEDERECIBO') || !texto.includes('DECLARACION')) {
    return { ...vacio, motivo: 'no_es_acuse' };
  }

  // Sin Ñ en la clase a propósito: `normalizar` ya la descompuso a N.
  const rfc = texto.match(/RFC:([A-Z&]{3,4}\d{6}[A-Z0-9]{3})/)?.[1] || null;
  const period = leerPeriodo(texto);
  const tipo = texto.match(/TIPODEDECLARACION:(NORMAL|COMPLEMENTARIA)/)?.[1] || null;
  const conceptos = leerConceptos(texto);

  let motivo = null;
  if (!rfc || !period) motivo = 'sin_identificacion';
  else if (conceptos.length === 0) motivo = 'sin_conceptos';

  return {
    rfc,
    period,
    tipo: tipo ? tipo.toLowerCase() : null,
    conceptos,
    motivo,
  };
};

/**
 * Lee un acuse y devuelve su interpretación.
 *
 * El resultado se reporta al servidor tal cual: él valida el RFC y el periodo
 * contra lo que ya sabe del acuse antes de guardarlo.
 *
 * @param {Blob} blob PDF descargado
 */
export const interpretarAcuse = async (blob) => {
  const { texto, origen } = await extraerTexto(blob);
  return { ...interpretarTexto(texto), origen };
};
